import { useMemo, useState } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/feature/LoadingState";
import { useCrew } from "@/hooks/admin/useAdminData";
import type { CrewMember } from "@/mocks/admin/crew";
import CrewCard from "./components/CrewCard";
import AddCrewModal from "./components/AddCrewModal";

const roleFilters = ["All", "Lead Mover", "Mover", "Driver", "Mover / Driver"];
const statusFilters = [
  { key: "all", label: "All Crew" },
  { key: "active", label: "Active" },
  { key: "off", label: "Off Today" },
  { key: "training", label: "Training" },
  { key: "on-leave", label: "On Leave" },
];

export default function CrewPage() {
  const [search, setSearch] = useState("");
  const [activeRole, setActiveRole] = useState("All");
  const [activeStatus, setActiveStatus] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState("");
  const { data: crewMembers, loading, error, refetch } = useCrew();

  const handleCreated = async (member: CrewMember) => {
    await refetch();
    setActiveStatus(member.status);
    setToast(`${member.name} added to the roster`);
    setTimeout(() => setToast(""), 3500);
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: crewMembers.length };
    crewMembers.forEach((m) => {
      c[m.status] = (c[m.status] || 0) + 1;
    });
    return c;
  }, [crewMembers]);

  const filtered = useMemo(() => {
    let list = crewMembers;
    if (activeStatus !== "all") list = list.filter((m) => m.status === activeStatus);
    if (activeRole !== "All") list = list.filter((m) => m.role === activeRole);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.id.toLowerCase().includes(q) ||
          m.skills.some((s) => s.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [search, activeRole, activeStatus, crewMembers]);

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-ink-500 mb-2">
              <span>People</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-ink-900 font-semibold">Crew</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 leading-tight">
              Crew Roster
            </h1>
            <p className="text-sm text-ink-500 mt-1.5 max-w-xl">
              Everyone on the Haulmark team — their availability, performance, and where they are today.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-line"></i>
              Schedule
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i>
              Add Crew Member
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-ink-100 rounded-lg">
          <div className="flex items-center gap-1 px-3 pt-2 border-b border-ink-100 overflow-x-auto scrollbar-thin">
            {statusFilters.map((f) => {
              const active = f.key === activeStatus;
              return (
                <button
                  key={f.key}
                  onClick={() => setActiveStatus(f.key)}
                  className={`relative px-3 py-3 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
                    active ? "text-ink-900" : "text-ink-500 hover:text-ink-700"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {f.label}
                    <span
                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        active ? "bg-ink-900 text-white" : "bg-cream-100 text-ink-600"
                      }`}
                    >
                      {counts[f.key] || 0}
                    </span>
                  </span>
                  {active && (
                    <span className="absolute left-3 right-3 -bottom-px h-0.5 bg-brand-500 rounded-full"></span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3">
            <div className="relative flex-1 min-w-0">
              <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, ID, or skill (e.g. Piano, CDL-A)…"
                className="w-full text-sm pl-10 pr-3 py-2.5 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
              />
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {roleFilters.map((r) => (
                <button
                  key={r}
                  onClick={() => setActiveRole(r)}
                  className={`text-xs font-semibold px-2.5 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                    activeRole === r
                      ? "bg-ink-900 text-white"
                      : "bg-cream-100 text-ink-600 hover:bg-ink-100"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Cards grid */}
        {loading ? (
          <LoadingState message="Loading crew…" />
        ) : error ? (
          <div className="bg-white border border-ink-100 rounded-lg py-20 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 text-red-400 mx-auto mb-3">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <div className="font-display text-lg font-bold text-ink-900">Failed to load crew</div>
            <div className="text-sm text-ink-500 mt-1">{error}</div>
            <button
              onClick={refetch}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Retry
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-ink-100 rounded-lg py-20 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-cream-100 text-ink-400 mx-auto mb-3">
              <i className="ri-user-search-line text-2xl"></i>
            </div>
            <div className="font-display text-lg font-bold text-ink-900">No crew found</div>
            <div className="text-sm text-ink-500 mt-1">Try a different filter or search term.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((m) => (
              <CrewCard key={m.id} member={m} />
            ))}
          </div>
        )}
      </div>

      <AddCrewModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={handleCreated}
      />

      <div
        className={`fixed bottom-6 right-6 z-[60] transition-all duration-300 ${
          toast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md border border-ink-800 flex items-center gap-2 max-w-sm">
          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
            <i className="ri-check-line text-sm"></i>
          </span>
          {toast}
        </div>
      </div>
    </AdminLayout>
  );
}